import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import { CalendarBlank, CaretLeft, CaretRight, Funnel, Folder, ListChecks } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const STATUS_COLORS = {
  not_started: "#94a3b8", assigned: "#3b82f6", in_progress: "#f59e0b",
  completed_by_vendor: "#14b8a6", approved: "#10b981", rejected: "#ef4444",
};
const STATUS_LABELS = {
  not_started: "Not Started", assigned: "Assigned", in_progress: "In Progress",
  completed_by_vendor: "Review", approved: "Approved", rejected: "Rejected",
};

export default function ProjectTimelinePage() {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState("all");
  const [weekOffset, setWeekOffset] = useState(0);

  useEffect(() => {
    Promise.all([
      API.get("/projects?limit=200").then(({ data }) => setProjects(data.projects || [])),
      API.get("/tasks?limit=500").then(({ data }) => setTasks(data.tasks || [])),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const today = useMemo(() => new Date(), []);

  // Generate 8-week window
  const weekStart = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() - d.getDay() + 1 + weekOffset * 7 * 4);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [today, weekOffset]);

  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 56; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [weekStart]);

  const weeks = useMemo(() => {
    const w = [];
    for (let i = 0; i < days.length; i += 7) {
      w.push({ start: days[i], days: days.slice(i, i + 7) });
    }
    return w;
  }, [days]);

  // Filter tasks with dates
  const filteredTasks = useMemo(() => {
    let filtered = tasks.filter(t => t.due_date || t.created_at);
    if (projectFilter !== "all") filtered = filtered.filter(t => t.project_id === projectFilter);
    return filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }, [tasks, projectFilter]);

  // Group by project
  const groupedByProject = useMemo(() => {
    const map = new Map();
    filteredTasks.forEach(t => {
      const pid = t.project_id || "unassigned";
      if (!map.has(pid)) map.set(pid, []);
      map.get(pid).push(t);
    });
    return map;
  }, [filteredTasks]);

  const getBarStyle = (task) => {
    const startDate = new Date(task.created_at);
    const endDate = task.due_date ? new Date(task.due_date) : new Date(startDate.getTime() + 7 * 86400000);
    const timelineStart = days[0].getTime();
    const timelineEnd = days[days.length - 1].getTime();
    const totalMs = timelineEnd - timelineStart;

    const barStart = Math.max(0, ((startDate.getTime() - timelineStart) / totalMs) * 100);
    const barEnd = Math.min(100, ((endDate.getTime() - timelineStart) / totalMs) * 100);
    const barWidth = Math.max(1.5, barEnd - barStart);

    return { left: `${barStart}%`, width: `${barWidth}%` };
  };

  const todayPos = useMemo(() => {
    const timelineStart = days[0]?.getTime();
    const timelineEnd = days[days.length - 1]?.getTime();
    if (!timelineStart || !timelineEnd) return null;
    const pos = ((today.getTime() - timelineStart) / (timelineEnd - timelineStart)) * 100;
    return pos >= 0 && pos <= 100 ? pos : null;
  }, [days, today]);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="project-timeline-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Project Timeline</h1>
          <p className="text-sm text-muted-foreground mt-1">{filteredTasks.length} tasks across {groupedByProject.size} project{groupedByProject.size !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-[200px]" data-testid="timeline-project-filter"><Funnel size={14} className="mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="outline" size="sm" onClick={() => setWeekOffset(p => p - 1)} data-testid="timeline-prev">
          <CaretLeft size={14} className="mr-1" /> Previous
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setWeekOffset(0)}>Today</Button>
        <Button variant="outline" size="sm" onClick={() => setWeekOffset(p => p + 1)} data-testid="timeline-next">
          Next <CaretRight size={14} className="ml-1" />
        </Button>
      </div>

      {/* Gantt Grid */}
      <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="timeline-gantt">
        {/* Header with weeks and days */}
        <div className="flex border-b border-border bg-muted/30 sticky top-0 z-10">
          <div className="w-[220px] shrink-0 px-3 py-2 border-r border-border">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Task / Project</span>
          </div>
          <div className="flex-1 relative">
            <div className="flex">
              {weeks.map((w, wi) => (
                <div key={wi} className="flex-1 border-r border-border last:border-r-0">
                  <div className="text-center text-[9px] font-semibold text-muted-foreground py-1 border-b border-border/50">
                    {w.start.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                  </div>
                  <div className="flex">
                    {w.days.map((d, di) => {
                      const isToday = d.toDateString() === today.toDateString();
                      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                      return (
                        <div key={di} className={`flex-1 text-center text-[8px] py-0.5 border-r border-border/30 last:border-r-0 ${isToday ? "bg-primary/10 font-bold text-primary" : isWeekend ? "bg-muted/40 text-muted-foreground/50" : "text-muted-foreground"}`}>
                          {["S","M","T","W","T","F","S"][d.getDay()]}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Task Rows grouped by project */}
        {filteredTasks.length === 0 ? (
          <div className="text-center py-12">
            <CalendarBlank size={36} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No tasks with dates to display</p>
          </div>
        ) : (
          Array.from(groupedByProject.entries()).map(([pid, ptasks]) => {
            const proj = projects.find(p => p.id === pid);
            return (
              <div key={pid}>
                {/* Project header row */}
                <div className="flex border-b border-border bg-muted/20">
                  <div className="w-[220px] shrink-0 px-3 py-1.5 border-r border-border">
                    <Link to={`/projects/${pid}`} className="flex items-center gap-1.5 text-xs font-semibold hover:text-primary transition-colors" data-testid={`timeline-proj-${pid}`}>
                      <Folder size={12} /> {proj?.name || "Unlinked"}
                    </Link>
                  </div>
                  <div className="flex-1" />
                </div>

                {/* Task rows */}
                {ptasks.map(task => {
                  const barStyle = getBarStyle(task);
                  const color = STATUS_COLORS[task.status] || "#94a3b8";
                  const isOverdue = task.due_date && new Date(task.due_date) < today && !["approved", "completed_by_vendor"].includes(task.status);
                  return (
                    <div key={task.id} className="flex border-b border-border/50 hover:bg-muted/10 transition-colors group" data-testid={`timeline-task-${task.id}`}>
                      <div className="w-[220px] shrink-0 px-3 py-2 border-r border-border flex items-center gap-2 min-h-[36px]">
                        <Link to={`/projects/${task.project_id}/tasks/${task.id}`} className="flex items-center gap-1.5 min-w-0 group-hover:text-primary transition-colors">
                          <ListChecks size={12} className="shrink-0" />
                          <span className="text-[11px] truncate">{task.title}</span>
                        </Link>
                        {isOverdue && <span className="text-[8px] px-1 py-0 rounded bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400 shrink-0">Late</span>}
                      </div>
                      <div className="flex-1 relative py-1.5">
                        <div
                          className="absolute top-1/2 -translate-y-1/2 h-5 rounded-sm transition-all cursor-pointer group-hover:h-6 group-hover:shadow-sm"
                          style={{ ...barStyle, backgroundColor: color, opacity: 0.85 }}
                          title={`${task.title} — ${STATUS_LABELS[task.status] || task.status}`}
                        >
                          <span className="text-[8px] text-white font-medium px-1 truncate block leading-5">{task.title}</span>
                        </div>
                        {/* Today line */}
                        {todayPos !== null && (
                          <div className="absolute top-0 bottom-0 w-px bg-primary/60 z-10" style={{ left: `${todayPos}%` }} />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-4 px-1">
        {Object.entries(STATUS_COLORS).map(([s, c]) => (
          <div key={s} className="flex items-center gap-1.5">
            <div className="w-3 h-2.5 rounded-sm" style={{ backgroundColor: c }} />
            <span className="text-[10px] text-muted-foreground">{STATUS_LABELS[s]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
